<template>
  <nav class="sticky top-0 z-50 border-b border-jackfruit-100/80 bg-jackfruit-50/88 backdrop-blur-xl">
    <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <div class="flex h-16 items-center justify-between gap-4">
        <router-link to="/" class="flex items-center gap-2.5">
          <span class="jackfruit-logo" aria-hidden="true">
            <img :src="jackfruitLogoSrc" alt="" class="jackfruit-logo__image" />
          </span>
          <span class="text-xl font-black tracking-tight text-ink-900">菠萝蜜</span>
        </router-link>

        <div class="hidden items-center gap-2 md:flex">
          <router-link
            v-for="item in navItems"
            :key="item.name"
            :to="item.to"
            class="rounded-lg px-3.5 py-2 text-sm font-bold text-ink-500 transition-all hover:bg-white hover:text-ink-900"
            :class="{ 'bg-white text-ink-900 shadow-sm': $route.name === item.name }"
          >
            {{ item.label }}
          </router-link>
        </div>

        <div class="hidden flex-1 justify-end md:flex">
          <div class="relative w-full max-w-xs">
            <input
              v-model="searchQuery"
              type="text"
              placeholder="搜书名、作者、爽点"
              class="w-full rounded-xl border border-jackfruit-100 bg-white/80 py-2 pl-10 pr-4 text-sm text-ink-900 outline-none transition-all placeholder:text-ink-300 focus:border-jackfruit-400 focus:bg-white focus:ring-2 focus:ring-jackfruit-200"
              @keyup.enter="searchBooks"
            />
            <span class="absolute left-3 top-2.5 text-ink-300">⌕</span>
          </div>
        </div>

        <div class="flex items-center gap-3">
          <div v-if="isAuthenticated" class="relative">
            <button
              class="honeybean-pill"
              aria-label="查看蜜豆余额"
              @click.stop="toggleHoneyBeanPanel"
            >
              <span class="honeybean-icon" aria-hidden="true">
                <span class="honeybean-icon__shine"></span>
              </span>
              <span class="text-xs font-black text-ink-900">{{ honeyBeanBalance }}</span>
            </button>

            <div v-if="showHoneyBeanPanel" class="absolute right-0 mt-2 w-56 overflow-hidden rounded-2xl border border-jackfruit-100 bg-white shadow-fruit">
              <div class="border-b border-jackfruit-100 px-4 py-3">
                <div class="flex items-center gap-2">
                  <span class="honeybean-icon honeybean-icon--sm" aria-hidden="true"></span>
                  <div>
                    <p class="text-sm font-black text-ink-900">{{ honeyBeanBalance }} 蜜豆</p>
                    <p class="mt-0.5 text-[11px] font-bold text-ink-400">6 蜜豆 = 1 元</p>
                  </div>
                </div>
              </div>
              <button class="block w-full px-4 py-3 text-left text-sm font-black text-ink-800 hover:bg-jackfruit-50" @click="openRecharge">
                充值蜜豆
              </button>
            </div>
          </div>

          <div v-if="isAuthenticated" class="relative">
            <button class="flex items-center gap-2 rounded-xl px-2 py-2" @click.stop="toggleUserMenu">
              <span class="grid h-8 w-8 place-items-center rounded-lg bg-jackfruit-100 text-sm font-black text-ink-900">
                {{ userInitials }}
              </span>
              <span class="hidden text-sm font-bold text-ink-700 sm:inline">{{ user?.nickname }}</span>
            </button>

            <div v-if="showUserMenu" class="absolute right-0 mt-2 w-52 overflow-hidden rounded-xl border border-ink-100 bg-white shadow-lg">
              <div class="border-b border-ink-100 px-4 py-3">
                <p class="text-sm font-bold text-ink-900">{{ user?.nickname }}</p>
                <p class="mt-1 text-xs text-ink-500">个人中心</p>
              </div>
              <router-link class="block px-4 py-2.5 text-left text-sm font-medium text-ink-700 hover:bg-jackfruit-50" to="/profile" @click="showUserMenu = false">
                我的创作
              </router-link>
              <button class="block w-full px-4 py-2.5 text-left text-sm font-medium text-ink-700 hover:bg-jackfruit-50" @click="logout">
                退出登录
              </button>
            </div>
          </div>

          <div v-else class="hidden items-center gap-2 sm:flex">
            <router-link to="/login" class="rounded-lg px-3 py-2 text-sm font-bold text-ink-600 hover:bg-white hover:text-ink-900">
              登录
            </router-link>
            <router-link to="/register" class="rounded-lg bg-jackfruit-500 px-4 py-2 text-sm font-black text-ink-900 shadow-sm hover:bg-jackfruit-600">
              开始阅读
            </router-link>
          </div>

        </div>
      </div>
    </div>

    <div v-if="showRechargeModal" class="fixed inset-0 z-[60] grid place-items-center bg-ink-900/35 px-4 backdrop-blur-sm" @click.self="showRechargeModal = false">
      <div class="w-full max-w-sm rounded-2xl border border-jackfruit-100 bg-white p-5 shadow-fruit">
        <div class="mb-4 flex items-center justify-between">
          <div>
            <h2 class="text-lg font-black text-ink-900">充值蜜豆</h2>
            <p class="mt-1 text-xs text-ink-500">6 蜜豆 = 1 元</p>
          </div>
          <button class="grid h-9 w-9 place-items-center rounded-xl bg-jackfruit-50 text-xl text-ink-500" @click="showRechargeModal = false">×</button>
        </div>

        <div class="grid grid-cols-3 gap-2">
          <button
            v-for="preset in rechargePresets"
            :key="preset"
            class="rounded-xl border px-3 py-3 text-center transition"
            :class="selectedRechargeYuan === preset ? 'border-jackfruit-400 bg-jackfruit-100 text-ink-900 shadow-sm' : 'border-ink-100 bg-white text-ink-600 hover:bg-jackfruit-50'"
            @click="selectedRechargeYuan = preset"
          >
            <div class="text-sm font-black">¥{{ preset }}</div>
            <div class="mt-1 text-[11px] font-bold text-ink-500">{{ preset * 6 }} 蜜豆</div>
          </button>
        </div>

        <div class="mt-4 grid grid-cols-2 gap-2">
          <button
            class="rounded-xl border px-3 py-2.5 text-sm font-bold transition"
            :class="selectedPaymentMethod === 'wechat' ? 'border-leaf-300 bg-leaf-50 text-leaf-700' : 'border-ink-100 text-ink-500'"
            @click="selectedPaymentMethod = 'wechat'"
          >
            微信
          </button>
          <button
            class="rounded-xl border px-3 py-2.5 text-sm font-bold transition"
            :class="selectedPaymentMethod === 'alipay' ? 'border-blue-300 bg-blue-50 text-blue-700' : 'border-ink-100 text-ink-500'"
            @click="selectedPaymentMethod = 'alipay'"
          >
            支付宝
          </button>
        </div>

        <button
          class="mt-4 w-full rounded-xl bg-jackfruit-500 py-3 text-sm font-black text-ink-900 shadow-fruit transition hover:bg-jackfruit-600 disabled:opacity-60"
          :disabled="isRecharging"
          @click="rechargeHoneyBeans"
        >
          {{ isRecharging ? '充值中...' : `模拟充值 ${selectedRechargeYuan * 6} 蜜豆` }}
        </button>
        <p v-if="rechargeMessage" class="mt-3 text-center text-xs font-bold text-leaf-600">{{ rechargeMessage }}</p>
      </div>
    </div>
  </nav>

  <div v-if="$route.name !== 'reading'" class="fixed inset-x-0 bottom-0 z-50 border-t border-jackfruit-100/70 bg-white/94 px-2 pb-[max(env(safe-area-inset-bottom),0.25rem)] pt-1 shadow-[0_-8px_22px_rgba(92,58,18,0.06)] backdrop-blur-xl md:hidden">
      <div class="mx-auto grid max-w-md grid-cols-4">
        <router-link
          v-for="item in bottomTabItems"
          :key="item.name"
          :to="item.to"
          class="flex min-h-[2.55rem] flex-col items-center justify-center gap-0.5 text-[11px] font-black transition"
          :class="$route.name === item.name ? 'text-jackfruit-600' : 'text-ink-400'"
        >
          <span class="bottom-tab-icon" aria-hidden="true" v-html="item.icon"></span>
          <span>{{ item.label }}</span>
        </router-link>
      </div>
    </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { paymentApi } from '@/api'

const router = useRouter()
const authStore = useAuthStore()
const jackfruitLogoSrc = 'data:image/png;base64,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'

const searchQuery = ref('')
const showUserMenu = ref(false)
const showHoneyBeanPanel = ref(false)
const showRechargeModal = ref(false)
const selectedRechargeYuan = ref(30)
const selectedPaymentMethod = ref<'wechat' | 'alipay'>('wechat')
const isRecharging = ref(false)
const rechargeMessage = ref('')
const rechargePresets = [6, 30, 68]

const isAuthenticated = computed(() => authStore.isAuthenticated)
const user = computed(() => authStore.user)
const userInitials = computed(() => user.value?.nickname?.charAt(0).toUpperCase() || '菠')
const honeyBeanBalance = computed(() => Math.floor(user.value?.balance || 0))

const navItems = [
  { name: 'home', label: '首页', to: '/' },
  { name: 'books', label: '书库', to: '/books' },
  { name: 'bookshelf', label: '书架', to: '/bookshelf' },
]

const bottomTabItems = computed(() => [
  {
    name: 'home',
    label: '首页',
    icon: '<svg viewBox="0 0 24 24"><path d="M3.5 10.4 12 3.5l8.5 6.9"/><path d="M5.8 9.3v10.2h12.4V9.3"/><path d="M9.4 19.5v-5.6h5.2v5.6"/></svg>',
    to: '/'
  },
  {
    name: 'books',
    label: '书库',
    icon: '<svg viewBox="0 0 24 24"><path d="M4.7 5.4c2.4-.8 4.9-.5 7.3.9v13.2c-2.4-1.4-4.9-1.7-7.3-.9z"/><path d="M19.3 5.4c-2.4-.8-4.9-.5-7.3.9v13.2c2.4-1.4 4.9-1.7 7.3-.9z"/></svg>',
    to: '/books'
  },
  {
    name: 'bookshelf',
    label: '书架',
    icon: '<svg viewBox="0 0 24 24"><path d="M4.5 19.2h15"/><path d="M6.2 5.1h3.1v11.8H6.2z"/><path d="M10.8 6.3h3.1v10.6h-3.1z"/><path d="m15 6.2 2.9-.8 2.9 10.9-2.9.8z"/></svg>',
    to: isAuthenticated.value ? '/bookshelf' : '/login'
  },
  {
    name: 'profile',
    label: '我的',
    icon: '<svg viewBox="0 0 24 24"><path d="M12 12.2a4 4 0 1 0 0-8 4 4 0 0 0 0 8Z"/><path d="M4.8 20.2c1.1-3.6 3.5-5.4 7.2-5.4s6.1 1.8 7.2 5.4"/></svg>',
    to: isAuthenticated.value ? '/profile' : '/login'
  }
])

const toggleUserMenu = () => {
  showUserMenu.value = !showUserMenu.value
  showHoneyBeanPanel.value = false
}

const toggleHoneyBeanPanel = () => {
  showHoneyBeanPanel.value = !showHoneyBeanPanel.value
  showUserMenu.value = false
}

const searchBooks = () => {
  const query = searchQuery.value.trim()
  if (!query) return
  router.push({ name: 'books', query: { search: query } })
  searchQuery.value = ''
}

const openRecharge = () => {
  showUserMenu.value = false
  showHoneyBeanPanel.value = false
  rechargeMessage.value = ''
  showRechargeModal.value = true
}

const rechargeHoneyBeans = async () => {
  if (!isAuthenticated.value) {
    router.push('/login')
    return
  }

  try {
    isRecharging.value = true
    rechargeMessage.value = ''
    const response = await paymentApi.rechargeMock({
      amountYuan: selectedRechargeYuan.value,
      paymentMethod: selectedPaymentMethod.value
    })
    if (response.code === 200 && response.data) {
      authStore.updateBalance(response.data.balance)
      rechargeMessage.value = `已到账 ${response.data.honeyBeans} 蜜豆`
      setTimeout(() => {
        showRechargeModal.value = false
      }, 700)
    }
  } catch (err: any) {
    rechargeMessage.value = err.message || '充值失败'
  } finally {
    isRecharging.value = false
  }
}

const logout = () => {
  authStore.logout()
  showUserMenu.value = false
}

const handleClickOutside = (event: MouseEvent) => {
  const target = event.target as HTMLElement
  if (!target.closest('nav')) {
    showUserMenu.value = false
    showHoneyBeanPanel.value = false
  }
}

onMounted(() => document.addEventListener('click', handleClickOutside))
onUnmounted(() => document.removeEventListener('click', handleClickOutside))
</script>

<style scoped>
.jackfruit-logo {
  position: relative;
  display: inline-grid;
  height: 38px;
  width: 38px;
  place-items: center;
  border-radius: 14px;
  background: linear-gradient(145deg, #fffdf2 0%, #fff1bd 100%);
  box-shadow: 0 12px 26px rgba(255, 170, 31, 0.24);
  overflow: hidden;
}

.jackfruit-logo__image {
  height: 100%;
  width: 100%;
  object-fit: cover;
}

.honeybean-pill {
  display: inline-flex;
  align-items: center;
  gap: 0.28rem;
  height: 2.15rem;
  border-radius: 999px;
  border: 1px solid rgba(255, 193, 7, 0.42);
  background: rgba(255, 248, 224, 0.92);
  padding: 0.28rem 0.58rem 0.28rem 0.32rem;
  box-shadow: 0 10px 24px rgba(255, 170, 31, 0.18);
}

.honeybean-icon {
  position: relative;
  display: inline-block;
  height: 1.55rem;
  width: 1.55rem;
  flex: 0 0 auto;
  border-radius: 50% 48% 52% 46%;
  background:
    radial-gradient(circle at 38% 34%, rgba(255, 255, 255, 0.72) 0 0.2rem, transparent 0.22rem),
    linear-gradient(145deg, #fff07a 0%, #ffc233 48%, #ff9824 100%);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.7),
    0 5px 10px rgba(255, 170, 31, 0.24);
  transform: rotate(-12deg);
}

.honeybean-icon--sm {
  height: 1.45rem;
  width: 1.45rem;
}

.honeybean-icon__shine {
  position: absolute;
  right: 0.31rem;
  top: 0.3rem;
  height: 0.22rem;
  width: 0.42rem;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.68);
  transform: rotate(22deg);
}

.bottom-tab-icon {
  display: inline-grid;
  height: 1.18rem;
  width: 1.18rem;
  place-items: center;
}

.bottom-tab-icon :deep(svg) {
  height: 100%;
  width: 100%;
  fill: none;
  stroke: currentColor;
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke-width: 1.9;
}
</style>
